import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import styles from './Datepicker.module.scss';

interface DatePresetsProps {
  isRange?: boolean;
  // appends the "All time" preset (clears both bounds) — only for pickers whose
  // empty state means "the whole history"
  showAllTime?: boolean;
  onChange: (start: Date | null, end: Date | null) => void;
}

interface Preset {
  label: string;
  start: Date | null;
  end: Date | null;
}

export const DatePresets: React.FC<DatePresetsProps> = ({ isRange=false, showAllTime = false, onChange = () => {} }) => {
  const { t } = useTranslation();

  const today = new Date();
  const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
  const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });

  const presets: Preset[] = [
    {
      label: t('Today'),
      start: today,
      end: today,
    },
    {
      label: t('7 days'),
      start: subDays(today, 6),
      end: today,
    },
    {
      label: t('14 days'),
      start: subDays(today, 13),
      end: today,
    },
    {
      label: t('30 days'),
      start: subDays(today, 29),
      end: today,
    },
    {
      label: t('90 days'),
      start: subDays(today, 89),
      end: today,
    },
    {
      label: t('This week'),
      start: startOfWeek(today, { weekStartsOn: 1 }),
      end: today,
    },
    {
      label: t('Last week'),
      start: lastWeekStart,
      end: lastWeekEnd,
    },
    {
      label: t('This month'),
      start: startOfMonth(today),
      end: today,
    },
    {
      label: t('This year'),
      start: startOfYear(today),
      end: today,
    },
    ...(showAllTime
      ? [
          {
            label: t('All time'),
            start: null,
            end: null,
          },
        ]
      : []),
  ];

  const handlePresetClick = (preset: Preset) => {
    onChange(preset.start, preset.end);
  };

  return (
    <div className={styles.datePresetsContainer}>
      {/* <span className={styles.presetsTitle}>{isRange ? t('Period presets:') : t('Date presets:')}</span> */}
      {presets.map((preset) => (
        <button
          key={preset.label}
          className={styles.datePresetButton}
          onClick={() => handlePresetClick(preset)}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}